// Data layer of the סקירה (overview) tab — standalone (non-hook) fetchers +
// sessionCache keys, so `preloadOverviewTab` can warm everything from a
// contacts-table row hover without any React context. Auth for the REST call
// follows the proven pattern in src/services/signatures.ts (firebase current
// user token, no React involved).
//
// SHARED cache keys (identical key == identical value type across tabs):
//   `contact-details:{contactId}` — the POST /api/v1/contacts/get-details
//     response (also used by the timeline tab — same fetcher shape, no
//     dataSources subset, so whichever tab fetches first serves both).
//   `notes:{contactId}` — UserNote[] sorted newest-first (also the notes tab).
// Overview-private keys are namespaced `overview:*`.
import { db, firebaseApp } from "firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { GenericConverter } from "utils/firestoreConverters";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import { latestBulkMessageTime } from "utils/contacts/lastBulkMessage";
import { fetchCached } from "../../data/sessionCache";
import {
  USER_NOTES_COLLECTION_NAME,
  CONTACT_FAVORITE_LISTINGS_COLLECTION_NAME,
  LISTING_PAGE_VIEWS_COLLECTION_NAME,
  systemNoteSummary
} from "../../../../../../@types/sharedSchema";
import type {
  UserNote,
  UserNotesDocument,
  RealEstateContact,
  FavoriteListingsOfContact,
  ListingPageViewsDocument,
  CallSummary,
  CallTranscriptAndSummary,
  REST_schema
} from "../../../../../../@types/sharedSchema";
import type { FirestoreListingHit } from "utils/listings/fetchListings";
import type { DisplayUserNote } from "hooks/useListingNotes";

// ── Cache keys ───────────────────────────────────────────────────────────────

export const contactDetailsCacheKey = (contactId: string): string =>
  `contact-details:${contactId}`;
export const notesCacheKey = (contactId: string): string => `notes:${contactId}`;
export const favoritesCacheKey = (contactId: string): string => `overview:${contactId}:favorites`;
export const latestAiCallCacheKey = (contactId: string): string =>
  `overview:${contactId}:latest-ai-call`;
export const pageViewsCacheKey = (favoritesDocId: string): string =>
  `overview:page-views:${favoritesDocId}`;
// Sorted so the key is a stable identity of the id SET — the caller's display
// order shifts when the async pageViews doc lands (engagement re-rank), and a
// permutation must not read as a different key (refetch + skeleton flash).
export const minisiteListingsCacheKey = (listingIds: string[]): string =>
  `overview:minisite-listings:${[...listingIds].sort().join("|")}`;
const userVirtualNumbersCacheKey = (email: string): string => `overview:user-vns:${email}`;

// Freshness budgets. Values are read instantly from cache regardless — these
// only control how eagerly the background revalidation kicks in.
export const CONTACT_DETAILS_MAX_AGE_MS = 60_000;
export const FAVORITES_MAX_AGE_MS = 60_000;
export const LATEST_AI_CALL_MAX_AGE_MS = 5 * 60_000;
// Hover-preload freshness for the latest-AI-call scan (see preloadOverview) —
// transcripts land minutes after a call ends, so 30 min staleness on the WARM
// path is invisible while cutting the table-sweep Firestore churn ~6×.
export const PRELOAD_LATEST_AI_CALL_MAX_AGE_MS = 30 * 60_000;
export const PAGE_VIEWS_MAX_AGE_MS = 2 * 60_000;

// ── Auth helpers ─────────────────────────────────────────────────────────────

const requireCurrentUserEmail = (): string => {
  const email = firebaseApp.auth().currentUser?.email;
  // Throwing (instead of returning a wrong-but-cacheable empty value) keeps the
  // cache clean: fetchCached stores the error, and the next call retries.
  if (!email) throw new Error("overviewData: no authenticated user yet");
  return email;
};

// ── Contact details (shared with the timeline tab) ───────────────────────────

export async function fetchContactDetailsResponse(
  contactId: string
): Promise<REST_schema.ContactDetailsResponseSchema> {
  const currentUser = firebaseApp.auth().currentUser;
  if (!currentUser) throw new Error("overviewData: no authenticated user yet");
  const token = await currentUser.getIdToken();
  const body: REST_schema.ContactDetailsRequestSchema = {
    phoneNumberInternational: contactId
  };
  const response = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/contacts/get-details`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`get-details failed with status ${response.status}`);
  }
  return (await response.json()) as REST_schema.ContactDetailsResponseSchema;
}

// ── Notes (shared with the notes tab) ────────────────────────────────────────

// Tolerant time parse: Firestore Timestamp (.toDate), Date, or ISO string.
export const parseNoteTime = (value: unknown): Date => {
  const maybeTimestamp = value as { toDate?: () => Date } | null | undefined;
  if (maybeTimestamp && typeof maybeTimestamp.toDate === "function") {
    return maybeTimestamp.toDate();
  }
  const parsed = new Date(value as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

export const sortNotesNewestFirst = <T extends { noteTime: Date }>(notes: T[]): T[] =>
  [...notes].sort((a, b) => (b.noteTime?.getTime() ?? 0) - (a.noteTime?.getTime() ?? 0));

/**
 * One-shot fetch of the current user's own notes doc for the contact (the
 * preload path — the mounted tab upgrades to a realtime listener that also
 * merges org-shared teammate notes, see useOverviewNotes).
 */
export async function fetchOwnNotesOnce(contactId: string): Promise<DisplayUserNote[]> {
  const email = requireCurrentUserEmail();
  const snapshot = await db
    .collection(USER_NOTES_COLLECTION_NAME)
    .where("userId", "==", email)
    .where("contactId", "==", contactId)
    .limit(1)
    .get();
  if (snapshot.empty) return [];
  const data = snapshot.docs[0].data() as UserNotesDocument;
  const parsed: DisplayUserNote[] = (data.notes ?? []).map((note: UserNote) => ({
    ...note,
    noteTime: parseNoteTime(note.noteTime),
    isMine: true,
    authorEmail: email
  }));
  return sortNotesNewestFirst(parsed);
}

// ── Minisite (favorite-listings-of-contact + listing-page-views) ─────────────

export async function fetchFavoritesDocOnce(
  contactId: string
): Promise<FavoriteListingsOfContact | null> {
  const email = requireCurrentUserEmail();
  const snapshot = await db
    .collection(CONTACT_FAVORITE_LISTINGS_COLLECTION_NAME)
    .where("realtorId", "==", email)
    .where("contactId", "==", contactId)
    .limit(1)
    .withConverter(GenericConverter<FavoriteListingsOfContact>())
    .get();
  return snapshot.empty ? null : snapshot.docs[0].data();
}

export async function fetchPageViewsDoc(
  favoritesDocId: string
): Promise<ListingPageViewsDocument | null> {
  if (!favoritesDocId) return null;
  const snapshot = await db
    .collection(LISTING_PAGE_VIEWS_COLLECTION_NAME)
    .doc(favoritesDocId)
    .withConverter(GenericConverter<ListingPageViewsDocument>())
    .get();
  return snapshot.exists ? (snapshot.data() as ListingPageViewsDocument) : null;
}

export async function fetchMinisiteListings(
  listingIds: string[]
): Promise<FirestoreListingHit[]> {
  if (!listingIds.length) return [];
  return fetchListingsByIds(listingIds);
}

// ── Latest transcribed (AI) call ─────────────────────────────────────────────

export type LatestAiCall = {
  call: CallSummary;
  transcript: CallTranscriptAndSummary;
};

async function fetchUserVirtualNumbers(email: string): Promise<string[]> {
  const snapshot = await db.doc(`dashboard-users-v2/${email}`).get();
  const virtualNumbers = (snapshot.data() as { virtualNumbers?: string[] } | undefined)
    ?.virtualNumbers;
  return Array.isArray(virtualNumbers) ? virtualNumbers : [];
}

const callTime = (call: CallSummary): number => {
  if (call.createdTime instanceof Date) return call.createdTime.getTime();
  const fromStartAt = call.startAt ? new Date(call.startAt).getTime() : NaN;
  return Number.isNaN(fromStartAt) ? 0 : fromStartAt;
};

/**
 * The newest call of this contact that has an AI transcript
 * (enrichment/transcriptAndSummary subdoc). Scoped to the current user's own
 * virtual numbers (org teammate calls belong to the calls tab). Uses the
 * existing (callerNumberInternational, createdTime desc) composite index.
 */
export async function fetchLatestTranscribedCall(
  contactId: string
): Promise<LatestAiCall | null> {
  const email = requireCurrentUserEmail();
  const virtualNumbers = await fetchCached(
    userVirtualNumbersCacheKey(email),
    () => fetchUserVirtualNumbers(email),
    { maxAgeMs: 10 * 60_000 }
  );
  if (!virtualNumbers.length) return null;

  const perNumberSnapshots = await Promise.all(
    virtualNumbers.map((virtualNumber) =>
      db
        .collection(`payCall-settings/${virtualNumber}/calls-summary`)
        .withConverter(GenericConverter<CallSummary>())
        .where("callerNumberInternational", "==", contactId)
        .orderBy("createdTime", "desc")
        .limit(5)
        .get()
        .catch(() => null)
    )
  );

  const candidates = perNumberSnapshots
    .flatMap((snapshot) => snapshot?.docs.map((doc) => doc.data()) ?? [])
    // Transcription runs only on answered calls — skip the rest up front.
    .filter((call) => call.result === "ANSWER")
    .sort((a, b) => callTime(b) - callTime(a))
    .slice(0, 6);

  for (const call of candidates) {
    try {
      const transcriptSnapshot = await db
        .collection(
          `payCall-settings/${call.virtualNumber}/calls-summary/${call.id}/enrichment`
        )
        .doc("transcriptAndSummary")
        .withConverter(GenericConverter<CallTranscriptAndSummary>())
        .get();
      const transcript = transcriptSnapshot.data();
      if (transcript && (transcript.summary || transcript.AI)) {
        return { call, transcript };
      }
    } catch {
      // A single unreadable transcript must not sink the whole card.
    }
  }
  return null;
}

// ── Last-touch derivation (מגע אחרון) ────────────────────────────────────────

export const TIMELINE_ACTION_LABELS: Record<string, string> = {
  "message-sent": "הודעה",
  call: "שיחה",
  meeting: "פגישה",
  task: "משימה",
  "favorite-listings-minisite-creation": "מיניסייט",
  note: "הערה",
  signature: "חתימה"
};

const toDateOrNull = (value: unknown): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export type LastTouchInfo = { time: Date; label: string };

/**
 * The freshest activity signal available in cache: newest timeline action
 * (calls / messages / meetings / minisite — server-aggregated, org-wide),
 * newest note, and the last bulk message. Best-effort — returns null when
 * nothing is known (yet).
 */
export function deriveLastTouch(
  contact: RealEstateContact,
  details: REST_schema.ContactDetailsResponseSchema | undefined,
  notes: ReadonlyArray<UserNote> | undefined
): LastTouchInfo | null {
  const candidates: LastTouchInfo[] = [];

  // Timeline arrives newest-first from the server; actionTime is an ISO string
  // over the wire despite the Date type — parse tolerantly. The server includes
  // FUTURE calendar events (actionTime = event.start, no clamp) — a meeting
  // next week is timeline[0]. "מגע אחרון" means the last touch that HAPPENED,
  // so skip future entries (and keep the 7-day stale warning honest).
  const now = Date.now();
  const timeline = details?.[0]?.timeline ?? [];
  for (const action of timeline) {
    const time = toDateOrNull(action.actionTime);
    if (!time || time.getTime() > now) continue;
    candidates.push({
      time,
      label: TIMELINE_ACTION_LABELS[action.actionType] ?? "פעילות"
    });
    break;
  }

  const newestNote = notes?.[0];
  const noteTime = newestNote ? toDateOrNull(newestNote.noteTime) : null;
  if (noteTime) candidates.push({ time: noteTime, label: "הערה" });

  // Both rails' stamps, through the one reader — the full page hands this the RAW document
  // and the CRM panel a post-processed one, and the two used to disagree (Trello #237).
  const bulkTime = toDateOrNull(latestBulkMessageTime(contact));
  if (bulkTime) candidates.push({ time: bulkTime, label: "דיוור" });

  if (!candidates.length) return null;
  return candidates.reduce((latest, candidate) =>
    candidate.time.getTime() > latest.time.getTime() ? candidate : latest
  );
}

// ── Recent-activity merge (server timeline + live notes) ────────────────────

/**
 * The פעילות אחרונה feed: the server timeline merged with the LIVE notes list,
 * so a note saved in the composer appears immediately (the server copy of the
 * `contact-details` aggregate only refreshes on revalidation).
 *
 * - Live notes become synthetic 'note' actions; a server 'note' entry with the
 *   same noteTime is dropped (the live copy wins), so when the server timeline
 *   catches up nothing duplicates — reconciliation is automatic.
 * - Future-dated server entries (scheduled meetings/tasks — the server stamps
 *   actionTime = event.start with no clamp) are filtered out: this feed shows
 *   activity that HAPPENED, mirroring the mock; the timeline tab shows all.
 * - Sorted newest-first.
 */
export function mergeNotesIntoTimeline(
  timeline: ReadonlyArray<REST_schema.ContactTimelineAction>,
  notes: ReadonlyArray<DisplayUserNote> | undefined,
  myDisplayName: string
): REST_schema.ContactTimelineAction[] {
  const now = Date.now();

  const noteTimes = new Set<number>();
  const syntheticNoteActions: REST_schema.ContactTimelineAction[] = [];
  for (const note of notes ?? []) {
    const time = toDateOrNull(note.noteTime);
    if (!time || time.getTime() > now) continue;
    noteTimes.add(time.getTime());
    const authorEmail = note.authorEmail ?? "";
    // Same display fallback the server rider uses for audio-only notes; a system comment (an
    // automated write's record) is ONE line, as the server's timeline shows it, under no name.
    const text = note.system && note.note?.trim() ? systemNoteSummary(note.note) : note.note?.trim() || "הערה קולית";
    syntheticNoteActions.push({
      actionType: "note",
      // A note written on the contact's PROPERTY (`usePropertyNotes`) says which property.
      actionDescription: note.listing
        ? `על הנכס${note.listing.label ? ` ${note.listing.label}` : ""}: ${text}`
        : text,
      actionTime: time,
      actionUser: {
        userId: authorEmail,
        userMail: authorEmail,
        userName: note.system ? "" : note.isMine === false ? note.authorName ?? authorEmail : myDisplayName,
        userAvatar: ""
      }
    });
  }

  const serverActions = timeline.filter((action) => {
    const time = toDateOrNull(action.actionTime);
    if (!time || time.getTime() > now) return false;
    // The server's copy of a note we already hold live — live copy wins.
    if (action.actionType === "note" && noteTimes.has(time.getTime())) return false;
    return true;
  });

  return [...syntheticNoteActions, ...serverActions].sort(
    (a, b) => (toDateOrNull(b.actionTime)?.getTime() ?? 0) - (toDateOrNull(a.actionTime)?.getTime() ?? 0)
  );
}

// ── Preload (row hover / page mount) ─────────────────────────────────────────

/**
 * Fire-and-forget warm-up of everything the overview renders. Idempotent-cheap:
 * every fetch dedups + stale-while-revalidates through fetchCached, so repeated
 * hovers over the same row cost nothing.
 */
export function preloadOverview(contact: RealEstateContact): void {
  const contactId = contact?.id;
  if (!contactId) return;

  void fetchCached(
    contactDetailsCacheKey(contactId),
    () => fetchContactDetailsResponse(contactId),
    { maxAgeMs: CONTACT_DETAILS_MAX_AGE_MS }
  ).catch(() => undefined);

  // Infinite maxAge ON THE PRELOAD PATH only: fetchOwnNotesOnce knows own notes
  // only, while the mounted hooks (useOverviewNotes/useContactNotes) mirror the
  // MERGED own+org list into this same key. A finite age here would let a later
  // row hover background-revalidate the merged value down to the own-only
  // subset (notes-tab badge shrink). Cold warm-up still fetches (no entry ⇒
  // fetch); once anything is cached, the live hooks own this key.
  void fetchCached(notesCacheKey(contactId), () => fetchOwnNotesOnce(contactId), {
    maxAgeMs: Number.POSITIVE_INFINITY
  }).catch(() => undefined);

  void fetchCached(
    favoritesCacheKey(contactId),
    () => fetchFavoritesDocOnce(contactId),
    { maxAgeMs: FAVORITES_MAX_AGE_MS }
  ).catch(() => undefined);

  // The latest-AI-call scan is the priciest warm-up (per-VN queries + up to 6
  // transcript doc reads when nothing is transcribed). On the HOVER path a much
  // longer freshness budget kills the every-5-minutes re-scan while sweeping
  // the table; the mounted card still revalidates at LATEST_AI_CALL_MAX_AGE_MS.
  void fetchCached(
    latestAiCallCacheKey(contactId),
    () => fetchLatestTranscribedCall(contactId),
    { maxAgeMs: PRELOAD_LATEST_AI_CALL_MAX_AGE_MS }
  ).catch(() => undefined);
}

// ── Small display helpers shared by the panels ───────────────────────────────

/** ₪2.9M for millions, ₪6,500 otherwise (mock s4/s6 budget chips). */
export function formatCompactBudget(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    const rounded = Math.round(millions * 10) / 10;
    return `₪${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}M`;
  }
  return `₪${value.toLocaleString("he-IL")}`;
}

export function listingAddressText(listing: FirestoreListingHit): string {
  const address = listing.address ?? ({} as Partial<typeof listing.address>);
  const streetPart = [address.street, address.houseNumber].filter(Boolean).join(" ");
  const areaPart = address.neighborhood || address.city || "";
  return [streetPart, areaPart].filter(Boolean).join(", ") || "נכס ללא כתובת";
}
